<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  initialAnswers: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false }
});

const emit = defineEmits(['complete']);

// Logical steps/baskets of questions for better focus
const steps = [
  {
    id: 'goals',
    title: 'Goals & timelines',
    description: 'You can capture up to three goals with their time horizons.',
    questions: ['goal1Type', 'goal1Horizon', 'goal2Type', 'goal2Horizon', 'goal3Type', 'goal3Horizon']
  },
  {
    id: 'risk',
    title: 'Risk profile & style',
    description: 'Your comfort with volatility and how you prefer the portfolio to be managed.',
    questions: ['riskTolerance', 'allocationStyle']
  },
  {
    id: 'tax',
    title: 'Tax & income needs',
    description: '80C needs, income slab and whether regular cash flows are important.',
    questions: ['taxStatus.needs80C', 'taxStatus.incomeSlab', 'incomeNeed']
  },
  {
    id: 'preferences',
    title: 'Portfolio preferences',
    description: 'Fine-tune exposure to small-caps, global, thematic and multi-asset funds.',
    questions: ['internationalExposure', 'excludeSmallCap', 'thematicInvesting', 'multiAssetPreference', 'fixedIncomeNeed']
  },
  {
    id: 'macro',
    title: 'Market & rate view',
    description: 'Your view on rates and markets helps us choose debt and equity sleeves.',
    questions: ['rateOutlook', 'arbitragePreference', 'marketCycle', 'diversificationPreference']
  }
];

// Full question dictionary (from previous version)
const allQuestions = {
  goal1Type: {
    id: 'goal1Type',
    label: 'Primary goal – what are you investing for?',
    type: 'select',
    options: [
      { value: 'Emergency', label: 'Emergency funds' },
      { value: 'ShortTerm', label: 'Short-term expense (Education / Marriage)' },
      { value: 'Wealth', label: 'Wealth growth' },
      { value: 'Retirement', label: 'Long term (e.g. Retirement)' }
    ],
    hint: 'Your main goal drives the anchor structure of the portfolio.',
    sidePoints: [
      'Emergency & short-term goals lean to liquid / low-risk debt funds.',
      'Wealth & retirement allow more equity and growth-oriented funds.',
      'Clear goals reduce impulsive, noise-driven decisions.'
    ],
    apiKey: 'goal1Type'
  },
  goal1Horizon: {
    id: 'goal1Horizon',
    label: 'Primary goal – how long can you stay invested?',
    type: 'select',
    options: [
      { value: '<3m', label: '0–3 months (emergency)' },
      { value: '3-12m', label: '3–12 months' },
      { value: '1-3y', label: '1–3 years' },
      { value: '3-5y', label: '3–5 years' },
      { value: '5+y', label: '5+ years' }
    ],
    hint: 'Short horizons favor liquid / arbitrage; long horizons unlock diversified equity anchors.',
    sidePoints: [
      'Emergency reserves usually sit in liquid funds (<3m).',
      '1–3 year goals are better aligned with short-duration / corporate bond funds.',
      '3–5+ years enable balanced, hybrid and equity-led portfolios.'
    ],
    apiKey: 'goal1Horizon'
  },
  goal2Type: {
    id: 'goal2Type',
    label: 'Second goal (optional) – what are you investing for?',
    type: 'select',
    options: [
      { value: 'Emergency', label: 'Emergency funds' },
      { value: 'ShortTerm', label: 'Short-term expense (Education / Marriage)' },
      { value: 'Wealth', label: 'Wealth growth' },
      { value: 'Retirement', label: 'Long term (e.g. Retirement)' }
    ],
    hint: 'Add another distinct goal if you have one.',
    sidePoints: [
      'You can skip this if you have only one primary goal.',
      'Multiple goals help us diversify timelines and risk better.'
    ],
    apiKey: 'goal2Type'
  },
  goal2Horizon: {
    id: 'goal2Horizon',
    label: 'Second goal – time horizon',
    type: 'select',
    options: [
      { value: '<3m', label: '0–3 months (emergency)' },
      { value: '3-12m', label: '3–12 months' },
      { value: '1-3y', label: '1–3 years' },
      { value: '3-5y', label: '3–5 years' },
      { value: '5+y', label: '5+ years' }
    ],
    hint: 'Use the closest bucket for when you will need this money.',
    sidePoints: [
      'Shorter horizons → more conservative fund categories.',
      'Longer horizons → more equity-oriented anchors.'
    ],
    apiKey: 'goal2Horizon'
  },
  goal3Type: {
    id: 'goal3Type',
    label: 'Third goal (optional) – what are you investing for?',
    type: 'select',
    options: [
      { value: 'Emergency', label: 'Emergency funds' },
      { value: 'ShortTerm', label: 'Short-term expense (Education / Marriage)' },
      { value: 'Wealth', label: 'Wealth growth' },
      { value: 'Retirement', label: 'Long term (e.g. Retirement)' }
    ],
    hint: 'Use this only if you have a third distinct goal.',
    sidePoints: [
      'Examples: children’s education, second home, legacy corpus.',
      'We aggregate across all goals when constructing the portfolio.'
    ],
    apiKey: 'goal3Type'
  },
  goal3Horizon: {
    id: 'goal3Horizon',
    label: 'Third goal – time horizon',
    type: 'select',
    options: [
      { value: '<3m', label: '0–3 months (emergency)' },
      { value: '3-12m', label: '3–12 months' },
      { value: '1-3y', label: '1–3 years' },
      { value: '3-5y', label: '3–5 years' },
      { value: '5+y', label: '5+ years' }
    ],
    hint: 'Pick the best-fit horizon for this third goal.',
    sidePoints: [
      'Goal horizons feed into anchor vs satellite sizing.',
      'Do not worry about being exact; an approximate bucket is fine.'
    ],
    apiKey: 'goal3Horizon'
  },
  riskTolerance: {
    id: 'riskTolerance',
    label: 'How comfortable are you with market ups and downs?',
    type: 'select',
    options: [
      { value: 'Low', label: 'Low' },
      { value: 'Moderate', label: 'Moderate' },
      { value: 'High', label: 'High' },
      { value: 'Very High', label: 'Very high' },
      { value: 'Not Sure', label: 'Not sure' }
    ],
    hint: 'This drives the equity vs debt mix and how aggressively we use small-cap / focused funds.',
    sidePoints: [
      'Low: prioritize capital safety and smoother returns.',
      'High / Very high: accept larger drawdowns for higher long-term growth.',
      'A good fit between your profile and portfolio avoids regret later.'
    ],
    apiKey: 'riskTolerance'
  },
  allocationStyle: {
    id: 'allocationStyle',
    label: 'Do you prefer dynamic allocation or a set allocation discipline?',
    type: 'select',
    options: [
      { value: 'Dynamic', label: 'Dynamic (tactical shifts)' },
      { value: 'Structured', label: 'Structured (rule-based mix)' },
      { value: 'HandsOff', label: 'Hands-off rebalancing' }
    ],
    hint: 'Dynamic allows managers to move between equity/debt; structured keeps a clearer rule-based split.',
    sidePoints: [
      'Dynamic works if you are comfortable with tactical calls by the fund manager.',
      'Structured keeps predictable exposure across market caps.',
      'Hands-off is for investors who want simple, auto-pilot rebalancing.'
    ],
    apiKey: 'allocationStyle'
  },
  'taxStatus.needs80C': {
    id: 'needs80C',
    label: 'Do you need tax savings under Section 80C (ELSS)?',
    type: 'toggle',
    hint: 'If yes, we consider an ELSS satellite inside the 80C limit.',
    sidePoints: [
      'ELSS invests in equity and has a 3-year lock-in.',
      'Up to ₹1.5 lakh per year can qualify under Section 80C.',
      'Ideal if you want both tax savings and long-term growth.'
    ],
    apiKey: 'taxStatus.needs80C'
  },
  'taxStatus.incomeSlab': {
    id: 'incomeSlab',
    label: 'What is your income slab?',
    type: 'select',
    options: [
      { value: 'Nil', label: 'Nil' },
      { value: '5%', label: '5%' },
      { value: '10%', label: '10%' },
      { value: '15%', label: '15%' },
      { value: '20%', label: '20%' },
      { value: '25%', label: '25%' },
      { value: '30%+', label: '30%+' }
    ],
    hint: 'We record your slab to reason about tax impact (for ELSS, dividend income, etc.).',
    sidePoints: [
      'Higher slabs benefit more from tax-efficient wrappers.',
      'ELSS and long-term capital gains can be more attractive at higher slabs.',
      'We do not compute tax here but use this to shape recommendations.'
    ],
    apiKey: 'taxStatus.incomeSlab'
  },
  incomeNeed: {
    id: 'incomeNeed',
    label: 'Would you like a sleeve that focuses on dividend / income?',
    type: 'toggle',
    hint: 'We may add dividend-yield / equity-savings exposure for periodic cash flows.',
    sidePoints: [
      'Dividend funds focus on companies with a history of payouts.',
      'Useful when income supplementation is an explicit goal.',
      'Capital growth may be slightly lower than aggressive growth funds.'
    ],
    apiKey: 'incomeNeed'
  },
  internationalExposure: {
    id: 'internationalExposure',
    label: 'Add global exposure to top international companies?',
    type: 'toggle',
    hint: 'If enabled, we consider a small satellite in global index / FoF schemes.',
    sidePoints: [
      'Helps diversify beyond domestic markets.',
      'Accesses global leaders via Indian mutual funds.',
      'Taxed as debt in India; we size this sleeve modestly.'
    ],
    apiKey: 'internationalExposure'
  },
  excludeSmallCap: {
    id: 'excludeSmallCap',
    label: 'Exclude dedicated small-cap exposure?',
    type: 'toggle',
    hint: 'Turn on if you wish to avoid small-cap volatility and stick to large/mid caps.',
    sidePoints: [
      'Small-caps can deliver high long-term returns but with sharp drawdowns.',
      'Conservative investors often prefer to exclude small-caps.',
      'You can still get diversified equity exposure without them.'
    ],
    apiKey: 'excludeSmallCap'
  },
  thematicInvesting: {
    id: 'thematicInvesting',
    label: 'Are you open to focused / thematic equity funds?',
    type: 'toggle',
    hint: 'We may add a focused fund sleeve only for higher risk profiles.',
    sidePoints: [
      'Focused funds run concentrated portfolios (fewer stocks).',
      'Suitable only when you are comfortable with higher volatility.',
      'We keep such allocations small and satellite-like.'
    ],
    apiKey: 'thematicInvesting'
  },
  multiAssetPreference: {
    id: 'multiAssetPreference',
    label: 'Interested in a single fund that blends equity, debt and commodities?',
    type: 'toggle',
    hint: 'Multi-asset funds reduce volatility by diversifying across asset classes.',
    sidePoints: [
      'Useful as a core anchor for medium/long-term goals.',
      'Helps hands-off investors get diversification without complexity.'
    ],
    apiKey: 'multiAssetPreference'
  },
  fixedIncomeNeed: {
    id: 'fixedIncomeNeed',
    label: 'Would you like a stable fixed-income sleeve?',
    type: 'toggle',
    hint: 'We may use corporate bond, banking & PSU, floater or short-duration funds.',
    sidePoints: [
      'Helps with capital preservation and smoother return profile.',
      'Useful for short-to-medium term goals and risk reduction.'
    ],
    apiKey: 'fixedIncomeNeed'
  },
  rateOutlook: {
    id: 'rateOutlook',
    label: 'How do you see interest rates over the next 1–2 years?',
    type: 'select',
    options: [
      { value: 'rising', label: 'Likely to rise' },
      { value: 'stable', label: 'Likely to stay stable' },
      { value: 'falling', label: 'Likely to fall' }
    ],
    hint: 'We use this to tilt the fixed-income sleeve (floater vs corporate vs PSU).',
    sidePoints: [
      'Rising rates favour floater funds over long-duration bonds.',
      'Falling rates benefit high-quality corporate / PSU bonds.',
      'A neutral view leads to a more balanced debt mix.'
    ],
    apiKey: 'rateOutlook'
  },
  arbitragePreference: {
    id: 'arbitragePreference',
    label: 'Interested in low-risk, tax-efficient arbitrage funds for parking money?',
    type: 'toggle',
    hint: 'Arbitrage funds exploit price gaps between cash & futures, taxed like equity.',
    sidePoints: [
      'Useful for 3–12 month parking with equity-like taxation.',
      'Still low-risk compared to pure equity funds.'
    ],
    apiKey: 'arbitragePreference'
  },
  marketCycle: {
    id: 'marketCycle',
    label: 'How would you describe the current market phase?',
    type: 'select',
    options: [
      { value: 'Overheated', label: 'Overheated' },
      { value: 'Correction', label: 'Correction' },
      { value: 'Recovery', label: 'Recovery' },
      { value: 'Neutral', label: 'Neutral / fairly valued' }
    ],
    hint: 'We use this to bias anchors between defensive and pro-growth categories.',
    sidePoints: [
      'Overheated → more dynamic / defensive anchors like BAF.',
      'Recovery → more pro-growth flexi / multi-cap exposure.',
      'Neutral → balanced mix without strong tilt.'
    ],
    apiKey: 'marketCycle'
  },
  diversificationPreference: {
    id: 'diversificationPreference',
    label: 'Prefer a broader set of funds or a simpler, focused set?',
    type: 'select',
    options: [
      { value: 'Simplicity', label: 'Keep it simple (fewer funds)' },
      { value: 'Broader', label: 'Broader diversification (more funds)' }
    ],
    hint: 'This controls whether we try to use more anchors + satellites or keep the list shorter.',
    sidePoints: [
      'Broader diversification can use up to 3 anchors + 3 satellites.',
      'Simplicity keeps the fund count tight while still meeting goals.'
    ],
    apiKey: 'diversificationPreference'
  }
};

const localAnswers = ref({ ...props.initialAnswers });

watch(
  () => props.initialAnswers,
  (val) => {
    localAnswers.value = { ...val };
  },
  { deep: true }
);

const currentStepIndex = ref(0);
const currentStep = computed(() => steps[currentStepIndex.value]);

const flatQuestions = computed(() => Object.values(allQuestions));

const isStepValid = computed(() => {
  const ids = currentStep.value.questions;
  return ids.every((qid) => {
    const q = allQuestions[qid];
    if (!q) return true;
    // Only enforce primary goal fields and non-toggle fields outside goals step
    if (currentStep.value.id === 'goals' && !['goal1Type', 'goal1Horizon'].includes(qid)) {
      return true; // secondary/tertiary goals are optional
    }
    if (q.type === 'toggle') return true;
    const key = q.apiKey || q.id;
    return !!localAnswers.value[key];
  });
});

const isAllValid = computed(() => {
  return flatQuestions.value.every((q) => {
    if (q.type === 'toggle') return true;
    // For goals, only require primary goal fields
    if (['goal2Type','goal2Horizon','goal3Type','goal3Horizon'].includes(q.id)) {
      return true;
    }
    const key = q.apiKey || q.id;
    return !!localAnswers.value[key];
  });
});

const activeQuestionId = ref(currentStep.value.questions[0]);
const activeQuestion = computed(() => allQuestions[activeQuestionId.value]);

const goNext = () => {
  if (!isStepValid.value || currentStepIndex.value >= steps.length - 1) return;
  currentStepIndex.value += 1;
  activeQuestionId.value = steps[currentStepIndex.value].questions[0];
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const goPrev = () => {
  if (currentStepIndex.value === 0) return;
  currentStepIndex.value -= 1;
  activeQuestionId.value = steps[currentStepIndex.value].questions[0];
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const progressPercent = computed(() => {
  const answered = flatQuestions.value.filter((q) => {
    if (q.type === 'toggle') return false; // optional
    const key = q.apiKey || q.id;
    return !!localAnswers.value[key];
  }).length;
  const total = flatQuestions.value.filter((q) => q.type !== 'toggle').length || 1;
  return Math.round((answered / total) * 100);
});

const buildGoals = () => {
  const goals = [];

  const mapSingle = (typeKey, horizonKey, index) => {
    const type = localAnswers.value[typeKey];
    const horizon = localAnswers.value[horizonKey] || '5+y';
    if (!type) return;
    let name = 'Wealth growth';
    if (type === 'Emergency') name = index === 0 ? 'Emergency' : `Emergency ${index + 1}`;
    else if (type === 'ShortTerm') name = index === 0 ? 'Short-term expense' : `Short-term expense ${index + 1}`;
    else if (type === 'Retirement') name = index === 0 ? 'Retirement' : `Retirement ${index + 1}`;
    goals.push({ name, horizon, amount: 500000 });
  };

  mapSingle('goal1Type', 'goal1Horizon', 0);
  mapSingle('goal2Type', 'goal2Horizon', 1);
  mapSingle('goal3Type', 'goal3Horizon', 2);

  return goals;
};

const mapToPayload = () => {
  const taxStatus = {
    needs80C: !!localAnswers.value['taxStatus.needs80C'],
    incomeSlab: localAnswers.value['taxStatus.incomeSlab'] || '30%+'
  };

  const incomeNeed = !!localAnswers.value.incomeNeed;

  return {
    clientId: 'web-ui',
    riskTolerance: localAnswers.value.riskTolerance || 'Moderate',
    allocationStyle: localAnswers.value.allocationStyle || 'Dynamic',
    excludeSmallCap: !!localAnswers.value.excludeSmallCap,
    incomeNeed,
    taxStatus,
    internationalExposure: !!localAnswers.value.internationalExposure,
    thematicInvesting: !!localAnswers.value.thematicInvesting,
    fixedIncomeNeed: !!localAnswers.value.fixedIncomeNeed,
    rateOutlook: localAnswers.value.rateOutlook || 'stable',
    multiAssetPreference: !!localAnswers.value.multiAssetPreference,
    arbitragePreference: !!localAnswers.value.arbitragePreference,
    diversificationPreference:
      localAnswers.value.diversificationPreference || 'Simplicity',
    marketCycle: localAnswers.value.marketCycle || 'Neutral',
    allocationMethod: 'heuristic',
    goals: buildGoals()
  };
};

const handleSubmit = () => {
  if (!isAllValid.value) return;
  emit('complete', mapToPayload());
};

const setAnswer = (q, value) => {
  const key = q.apiKey || q.id;
  localAnswers.value[key] = value;
  activeQuestionId.value = q.id;
};

const sidePanelRef = ref(null);
const questionRowRefs = ref({});

const onQuestionFocus = (qid) => {
  activeQuestionId.value = qid;
  const el = questionRowRefs.value[qid];
  if (el && sidePanelRef.value) {
    const container = document.querySelector('.mfq-layout');
    const containerTop = container ? container.getBoundingClientRect().top : 0;
    const rowTop = el.getBoundingClientRect().top - containerTop;
    sidePanelRef.value.style.transform = `translateY(${rowTop}px)`;
  }
};

const handleStepClick = (idx) => {
  if (idx <= currentStepIndex.value && !props.disabled) {
    currentStepIndex.value = idx;
    activeQuestionId.value = steps[idx].questions[0];
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};
</script>

<template>
  <div class="mfq-root">
    <div class="mfq-header">
      <div class="breadcrumbs">
        <span
          v-for="(stepItem, idx) in steps"
          :key="stepItem.id"
          :class="['crumb', { active: idx === currentStepIndex, done: idx < currentStepIndex }]"
          @click="handleStepClick(idx)"
        >
          <span class="crumb-index">{{ idx + 1 }}</span>
          <span class="crumb-label">{{ stepItem.title }}</span>
        </span>
      </div>
      <div class="progress-wrap">
        <span class="progress-text">{{ progressPercent }}% complete</span>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressPercent + '%' }" />
        </div>
      </div>
    </div>

    <div class="mfq-layout">
      <div class="mfq-main">
        <h2 class="step-title">{{ currentStep.title }}</h2>
        <p class="step-subtitle">{{ currentStep.description }}</p>

        <div class="section-body" v-if="currentStep.id === 'goals'">
          <!-- Goal 1 -->
          <div class="goal-row">
            <div
              class="goal-col"
              :ref="el => (questionRowRefs.goal1Type = el)"
              @mouseenter="onQuestionFocus('goal1Type')"
            >
              <label class="question-label">Goal 1</label>
              <p class="question-hint">Main purpose of this investment.</p>
              <select
                class="input-select"
                :disabled="disabled"
                :value="localAnswers[allQuestions.goal1Type.apiKey] || ''"
                @change="setAnswer(allQuestions.goal1Type, $event.target.value)"
                @mouseenter="onQuestionFocus('goal1Type')"
              >
                <option disabled value="">Select goal</option>
                <option v-for="opt in allQuestions.goal1Type.options" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div
              class="goal-col"
              :ref="el => (questionRowRefs.goal1Horizon = el)"
              @mouseenter="onQuestionFocus('goal1Horizon')"
            >
              <label class="question-label question-label--light">Horizon</label>
              <p class="question-hint">How long you can stay invested.</p>
              <select
                class="input-select"
                :disabled="disabled"
                :value="localAnswers[allQuestions.goal1Horizon.apiKey] || ''"
                @change="setAnswer(allQuestions.goal1Horizon, $event.target.value)"
                @mouseenter="onQuestionFocus('goal1Horizon')"
              >
                <option disabled value="">Select horizon</option>
                <option v-for="opt in allQuestions.goal1Horizon.options" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Goal 2 (optional) -->
          <div class="goal-row">
            <div
              class="goal-col"
              :ref="el => (questionRowRefs.goal2Type = el)"
              @mouseenter="onQuestionFocus('goal2Type')"
            >
              <label class="question-label">Goal 2 (optional)</label>
              <p class="question-hint">Add another distinct goal if any.</p>
              <select
                class="input-select"
                :disabled="disabled"
                :value="localAnswers[allQuestions.goal2Type.apiKey] || ''"
                @change="setAnswer(allQuestions.goal2Type, $event.target.value)"
                @mouseenter="onQuestionFocus('goal2Type')"
              >
                <option disabled value="">Select goal</option>
                <option v-for="opt in allQuestions.goal2Type.options" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div
              class="goal-col"
              :ref="el => (questionRowRefs.goal2Horizon = el)"
              @mouseenter="onQuestionFocus('goal2Horizon')"
            >
              <label class="question-label question-label--light">Horizon</label>
              <p class="question-hint">When you expect to use this money.</p>
              <select
                class="input-select"
                :disabled="disabled"
                :value="localAnswers[allQuestions.goal2Horizon.apiKey] || ''"
                @change="setAnswer(allQuestions.goal2Horizon, $event.target.value)"
                @mouseenter="onQuestionFocus('goal2Horizon')"
              >
                <option disabled value="">Select horizon</option>
                <option v-for="opt in allQuestions.goal2Horizon.options" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
          </div>

          <!-- Goal 3 (optional) -->
          <div class="goal-row">
            <div
              class="goal-col"
              :ref="el => (questionRowRefs.goal3Type = el)"
              @mouseenter="onQuestionFocus('goal3Type')"
            >
              <label class="question-label">Goal 3 (optional)</label>
              <p class="question-hint">Only if you have a third goal.</p>
              <select
                class="input-select"
                :disabled="disabled"
                :value="localAnswers[allQuestions.goal3Type.apiKey] || ''"
                @change="setAnswer(allQuestions.goal3Type, $event.target.value)"
                @mouseenter="onQuestionFocus('goal3Type')"
              >
                <option disabled value="">Select goal</option>
                <option v-for="opt in allQuestions.goal3Type.options" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <div
              class="goal-col"
              :ref="el => (questionRowRefs.goal3Horizon = el)"
              @mouseenter="onQuestionFocus('goal3Horizon')"
            >
              <label class="question-label question-label--light">Horizon</label>
              <p class="question-hint">Timeframe for this third goal.</p>
              <select
                class="input-select"
                :disabled="disabled"
                :value="localAnswers[allQuestions.goal3Horizon.apiKey] || ''"
                @change="setAnswer(allQuestions.goal3Horizon, $event.target.value)"
                @mouseenter="onQuestionFocus('goal3Horizon')"
              >
                <option disabled value="">Select horizon</option>
                <option v-for="opt in allQuestions.goal3Horizon.options" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div class="section-body" v-else>
          <div
            v-for="qid in currentStep.questions"
            :key="qid"
            class="question-row"
            :ref="el => (questionRowRefs[qid] = el)"
            @mouseenter="onQuestionFocus(qid)"
          >
            <!-- existing generic question rendering for non-goal steps -->
            <label class="question-label">{{ allQuestions[qid].label }}</label>
            <p v-if="allQuestions[qid].hint" class="question-hint">{{ allQuestions[qid].hint }}</p>
            <select
              v-if="allQuestions[qid].type === 'select'"
              class="input-select"
              :disabled="disabled"
              :value="localAnswers[allQuestions[qid].apiKey || allQuestions[qid].id] || ''"
              @change="setAnswer(allQuestions[qid], $event.target.value)"
            >
              <option disabled value="">Select an option</option>
              <option v-for="opt in allQuestions[qid].options" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
            <label v-else-if="allQuestions[qid].type === 'toggle'" class="toggle-wrapper">
              <input
                type="checkbox"
                :disabled="disabled"
                :checked="!!localAnswers[allQuestions[qid].apiKey || allQuestions[qid].id]"
                @change="setAnswer(allQuestions[qid], $event.target.checked)"
              />
              <span class="toggle-label">
                {{ localAnswers[allQuestions[qid].apiKey || allQuestions[qid].id]
                  ? 'Yes'
                  : '[Default is "No", click on checkbox to opt-in]' }}
              </span>
            </label>
          </div>
        </div>

        <div class="nav-actions">
          <button class="btn-secondary" type="button" @click="goPrev" :disabled="currentStepIndex === 0 || disabled">
            ← Previous
          </button>
          <button
            v-if="currentStepIndex < steps.length - 1"
            class="btn-primary"
            type="button"
            @click="goNext"
            :disabled="!isStepValid || disabled"
          >
            Next step →
          </button>
          <button
            v-else
            class="btn-primary"
            type="button"
            @click="handleSubmit"
            :disabled="!isAllValid || disabled"
          >
            Review my answers
          </button>
        </div>
      </div>

      <div class="mfq-side-panel" v-if="activeQuestion" ref="sidePanelRef">
        <h3 class="side-title">How to answer this?</h3>
        <p class="side-question">{{ activeQuestion.label }}</p>
        <ul class="side-points">
          <li v-for="(p, idx) in activeQuestion.sidePoints" :key="idx">{{ p }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
.mfq-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mfq-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.breadcrumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f3f5;
  font-size: 0.78rem;
  cursor: pointer;
  color: #868e96;
}

.crumb.active {
  background: var(--an-primary-light, #e3f2fd);
  color: var(--an-primary, #1976d2);
}

.crumb.done {
  background: #d3f9d8;
  color: #0f5132;
}

.crumb-index {
  font-weight: 700;
}

.crumb-label {
  font-weight: 600;
}

.progress-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-text {
  font-size: 0.78rem;
  color: #868e96;
}

.progress-bar {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: #e9ecef;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--an-primary), var(--an-secondary));
  transition: width 0.25s ease;
}

.mfq-layout {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(260px, 1.2fr);
  gap: 24px;
}

.mfq-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-title {
  font-size: 1.15rem;
  margin: 0;
}

.step-subtitle {
  margin: 0;
  font-size: 0.86rem;
  color: #868e96;
}

.section-body {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.question-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.question-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.question-label--light {
  font-weight: 500;
  color: #adb5bd;
}

.question-hint {
  margin: 0;
  font-size: 0.78rem;
  color: #868e96;
}

.input-select {
  margin-top: 4px;
  border-radius: 6px;
  border: 1px solid #ced4da;
  padding: 6px 8px;
  font-size: 0.85rem;
}

.toggle-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.toggle-label {
  font-size: 0.85rem;
}

.nav-actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.btn-primary,
.btn-secondary {
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
}

.btn-primary {
  background: var(--an-primary, #1976d2);
  color: #fff;
}

.btn-secondary {
  background: #f1f3f5;
  color: #343a40;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.mfq-side-panel {
  position: relative;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  padding: 14px 16px;
  background: #f8f9fa;
  align-self: flex-start;
  transition: transform 0.25s ease;
}

.side-title {
  margin: 0 0 6px 0;
  font-size: 0.95rem;
  font-weight: 700;
}

.side-question {
  margin: 0 0 8px 0;
  font-size: 0.85rem;
  color: #343a40;
}

.side-points {
  margin: 0;
  padding-left: 18px;
  font-size: 0.8rem;
  color: #495057;
}

.goal-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.goal-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

@media (max-width: 900px) {
  .mfq-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .mfq-side-panel {
    display: none;
  }

  .goal-row {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
